import React, { useMemo, useState } from 'react';
import { XIcon } from 'lucide-react';
function getLastUpdatedDate() {
  const d = new Date();
  d.setMonth(d.getMonth() - 2);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}
function Modal({
  title,
  isOpen,
  onClose,
  children
}: {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const lastUpdated = useMemo(() => getLastUpdatedDate(), []);
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-71">
      <div className="absolute inset-0 bg-navy/70 backdrop-blur-sm" onClick={onClose} data-id="element-72"></div>
      <div className="relative bg-navy border border-gold/20 rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-5 md:p-8" data-id="element-73">
        <div className="flex items-center justify-between mb-4" data-id="element-74">
          <h3 className="font-serif text-lg md:text-xl text-gold font-bold" data-id="element-75">
            {title}
          </h3>
          <button onClick={onClose} className="text-cream/40 hover:text-cream/80 transition-colors p-1" data-id="element-76">
            <XIcon size={18} data-id="element-77" />
          </button>
        </div>
        <div className="text-cream/70 text-sm md:text-base font-light space-y-3 leading-relaxed [&_strong]:text-gold [&_strong]:font-semibold" data-id="element-78">
          {children}
        </div>
        <div className="border-t border-gold/20 mt-5 pt-3" data-id="element-79">
          <p className="text-cream/30 text-[9px] md:text-[11px] font-light" data-id="element-80">
            Last updated: {lastUpdated}
          </p>
        </div>
      </div>
    </div>;
}
export function Footer() {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  return <>
      <footer className="py-4 md:py-5 px-4 md:px-6 bg-navy border-t border-gold/10 text-center" data-id="element-81">
        <div className="max-w-2xl mx-auto" data-id="element-82">
          <p className="text-cream/30 text-[10px] md:text-[11px] leading-relaxed font-light" data-id="element-83">
            © 2026 Who Programmed You to Spend? All rights reserved. This
            educational resource is not affiliated with or endorsed by Facebook
            and does not constitute legal, medical, or financial advice. Results
            vary; consult a qualified professional. By using this site you agree
            to our{' '}
            <button onClick={() => setShowPrivacy(true)} className="underline underline-offset-2 hover:text-cream/50 transition-colors" data-id="element-84">
              Privacy Policy
            </button>
            {' & '}
            <button onClick={() => setShowTerms(true)} className="underline underline-offset-2 hover:text-cream/50 transition-colors" data-id="element-85">
              Terms of Service
            </button>
            .
          </p>
        </div>
      </footer>

      <Modal title="Privacy Policy" isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} data-id="element-86">
        <p data-id="element-87">
          <strong className="text-navy" data-id="element-88">Information We Collect</strong>
        </p>
        <p data-id="element-89">
          We collect information you provide directly when you purchase or
          interact with our digital products, including your name and email
          address.
        </p>

        <p data-id="element-90">
          <strong className="text-navy" data-id="element-91">How We Use Your Information</strong>
        </p>
        <p data-id="element-92">
          Your information is used solely to deliver the product you purchased
          and to send relevant updates about your purchase. We do not sell,
          rent, or share your personal information with third parties for
          marketing purposes.
        </p>

        <p data-id="element-93">
          <strong className="text-navy" data-id="element-94">Cookies & Analytics</strong>
        </p>
        <p data-id="element-95">
          Our website may use cookies and analytics tools to understand how
          visitors interact with our content. This data is anonymized and used
          only to improve the user experience.
        </p>

        <p data-id="element-96">
          <strong className="text-navy" data-id="element-97">Data Security</strong>
        </p>
        <p data-id="element-98">
          We implement reasonable security measures to protect your personal
          information. However, no method of electronic transmission or storage
          is 100% secure.
        </p>

        <p data-id="element-99">
          <strong className="text-navy" data-id="element-100">Your Rights</strong>
        </p>
        <p data-id="element-101">
          You may request access to, correction of, or deletion of your personal
          data at any time by contacting us through our official channels.
        </p>

        <p data-id="element-102">
          <strong className="text-navy" data-id="element-103">Changes to This Policy</strong>
        </p>
        <p data-id="element-104">
          We may update this privacy policy from time to time. Any changes will
          be reflected on this page with an updated effective date.
        </p>
      </Modal>

      <Modal title="Terms of Service" isOpen={showTerms} onClose={() => setShowTerms(false)} data-id="element-105">
        <p data-id="element-106">
          <strong className="text-navy" data-id="element-107">Acceptance of Terms</strong>
        </p>
        <p data-id="element-108">
          By purchasing or accessing "Who Programmed You to Spend?" you agree to
          be bound by these terms of use.
        </p>

        <p data-id="element-109">
          <strong className="text-navy" data-id="element-110">Digital Product</strong>
        </p>
        <p data-id="element-111">
          This is a digital information product provided for educational and
          personal development purposes. It is not a substitute for professional
          financial advice, therapy, or counseling.
        </p>

        <p data-id="element-112">
          <strong className="text-navy" data-id="element-113">Intellectual Property</strong>
        </p>
        <p data-id="element-114">
          All content, text, graphics, and materials in this product are
          protected by copyright. You may not reproduce, distribute, or share
          this content without written permission.
        </p>

        <p data-id="element-115">
          <strong className="text-navy" data-id="element-116">No Guarantees</strong>
        </p>
        <p data-id="element-117">
          While this guide provides psychological insights and practical tools,
          individual results may vary. We do not guarantee specific financial
          outcomes.
        </p>

        <p data-id="element-118">
          <strong className="text-navy" data-id="element-119">Refund Policy</strong>
        </p>
        <p data-id="element-120">
          Due to the digital nature of this product, all sales are final. If you
          experience any issues with your purchase, please reach out and we will
          do our best to resolve them.
        </p>

        <p data-id="element-121">
          <strong className="text-navy" data-id="element-122">Limitation of Liability</strong>
        </p>
        <p data-id="element-123">
          The creator of this product shall not be held liable for any decisions
          made based on the information provided. Use of this material is at
          your own discretion and risk.
        </p>

        <p data-id="element-124">
          <strong className="text-navy" data-id="element-125">Changes to Terms</strong>
        </p>
        <p data-id="element-126">
          We reserve the right to modify these terms at any time. Continued use
          of the product constitutes acceptance of any changes.
        </p>
      </Modal>
    </>;
}